/**
 * <brick-cart-page>
 * Cart page behaviour (sections/main-cart.liquid). Mirrors the drawer:
 * - quantity +/- , typed quantity and remove → /cart/change.js with
 *   `sections`, one request returns the fresh section HTML;
 * - upsell "Add" forms ([data-cart-page-add], opted out of the drawer with
 *   [data-no-drawer]) → /cart/add.js, then the section is re-rendered;
 * - order note saved with /cart/update.js (debounced, no re-render);
 * - after every render dispatches `brick:cart:updated` (header bubble) with
 *   `source: 'cart-page'`, and refreshes itself when the drawer changes the
 *   cart on this page.
 * Without JS the form still posts `updates[]` / checkout to /cart.
 */
(() => {
  if (customElements.get('brick-cart-page')) return;

  const theme = (window.BrickTheme = window.BrickTheme || {});
  const root = (theme.routes && theme.routes.root) || '/';
  const motionOff = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const postJSON = async (path, body) => {
    const response = await fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || '');
    }
    return data;
  };

  class BrickCartPage extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.queue = Promise.resolve();

      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('input', (event) => this.onInput(event));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.addEventListener('submit', (event) => this.onSubmit(event));

      this.onExternalUpdate = (event) => {
        const detail = event.detail || {};
        if (detail.source === 'cart-page') return;
        this.refresh();
      };
      document.addEventListener('brick:cart:updated', this.onExternalUpdate);
    }

    disconnectedCallback() {
      document.removeEventListener('brick:cart:updated', this.onExternalUpdate);
    }

    get inner() {
      return this.querySelector('[data-cart-page-inner]');
    }

    /* ------------------------------------------------------------ */
    /* Events                                                         */
    /* ------------------------------------------------------------ */
    onClick(event) {
      const line = event.target.closest('[data-line]');
      if (!line) return;

      const step = event.target.closest('[data-qty-step]');
      if (step) {
        const input = line.querySelector('[data-qty-input]');
        const increment = parseInt(input.step, 10) || 1;
        const direction = parseInt(step.dataset.qtyStep, 10);
        const quantity = Math.max(0, (parseInt(input.value, 10) || 0) + direction * increment);
        input.value = quantity;
        this.change(line.dataset.key, quantity, direction > 0 ? 'plus' : 'minus');
        return;
      }

      if (event.target.closest('[data-remove]')) this.change(line.dataset.key, 0, 'remove');
    }

    onChange(event) {
      const input = event.target.closest('[data-qty-input]');
      if (!input) return;
      const line = input.closest('[data-line]');
      const quantity = Math.max(0, parseInt(input.value, 10) || 0);
      this.change(line.dataset.key, quantity, 'input');
    }

    onInput(event) {
      if (!event.target.matches('[data-cart-note]')) return;
      window.clearTimeout(this.noteTimer);
      const note = event.target.value;
      this.noteTimer = window.setTimeout(() => {
        postJSON('cart/update.js', { note }).catch(() => {});
      }, 450);
    }

    onKeydown(event) {
      if (event.key === 'Enter' && event.target.matches('[data-qty-input]')) {
        event.preventDefault();
        event.target.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    onSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-cart-page-add')) return;
      event.preventDefault();
      this.queue = this.queue.then(() => this.add(form, event.submitter)).catch(() => {});
    }

    /* ------------------------------------------------------------ */
    /* Cart API                                                       */
    /* ------------------------------------------------------------ */
    change(key, quantity, role) {
      this.queue = this.queue.then(() => this.runChange(key, quantity, role)).catch(() => {});
      return this.queue;
    }

    async runChange(key, quantity, role) {
      const line = this.findLine(key);
      if (line) line.classList.add('is-loading');
      this.setBusy(true);
      try {
        const cart = await postJSON('cart/change.js', {
          id: key,
          quantity,
          sections: [this.sectionId],
          sections_url: window.location.pathname,
        });
        const html = cart.sections && cart.sections[this.sectionId];
        if (!this.render(html, { key, role })) await this.refresh({ key, role });
      } catch (error) {
        const message = error.message || (this.inner && this.inner.dataset.errorText) || '';
        await this.refresh({ key, role });
        const slot = this.findLine(key)?.querySelector('[data-line-error]');
        if (slot && message) {
          slot.textContent = message;
          slot.hidden = false;
        }
      } finally {
        this.setBusy(false);
      }
    }

    async add(form, submitter) {
      const button = submitter || form.querySelector('[type="submit"]');
      if (button) {
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-busy', 'true');
      }
      form.querySelectorAll('.cart-add-error').forEach((el) => el.remove());
      const body = new FormData(form);
      body.append('sections', this.sectionId);
      body.append('sections_url', window.location.pathname);
      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.status) throw new Error(data.description || data.message || '');
        const html = data.sections && data.sections[this.sectionId];
        if (!this.render(html, { title: true })) await this.refresh({ title: true });
      } catch (error) {
        const slot = document.createElement('p');
        slot.className = 'field__error cart-add-error';
        slot.setAttribute('role', 'alert');
        slot.textContent = error.message || (this.inner && this.inner.dataset.errorText) || '';
        form.append(slot);
        if (button) {
          button.removeAttribute('aria-disabled');
          button.removeAttribute('aria-busy');
        }
      }
    }

    async refresh(focus) {
      try {
        const url = new URL(window.location.pathname, window.location.origin);
        url.searchParams.set('sections', this.sectionId);
        url.searchParams.set('_', String(Date.now()));
        const response = await fetch(url.toString(), { cache: 'no-store', credentials: 'same-origin' });
        if (!response.ok) return false;
        const data = await response.json();
        return this.render(data && data[this.sectionId], focus);
      } catch (error) {
        return false;
      }
    }

    setBusy(busy) {
      const inner = this.inner;
      if (!inner) return;
      if (busy) inner.setAttribute('aria-busy', 'true');
      else inner.removeAttribute('aria-busy');
    }

    /* ------------------------------------------------------------ */
    /* Rendering                                                      */
    /* ------------------------------------------------------------ */
    render(html, focus) {
      if (typeof html !== 'string') return false;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-cart-page-inner]');
      const current = this.inner;
      if (!next || !current) return false;

      const hadFocus = current.contains(document.activeElement);
      const noteWasOpen = Boolean(current.querySelector('[data-preserve-open][open]'));
      const noteValue = current.querySelector('[data-cart-note]')?.value;
      const oldFill = current.querySelector('[data-progress]');
      const oldProgress = oldFill ? oldFill.style.getPropertyValue('--progress') : '';

      const adopted = document.importNode(next, true);
      current.replaceWith(adopted);

      const note = adopted.querySelector('[data-cart-note]');
      if (note && typeof noteValue === 'string') note.value = noteValue;
      if (noteWasOpen) adopted.querySelector('[data-preserve-open]')?.setAttribute('open', '');

      const newFill = adopted.querySelector('[data-progress]');
      if (newFill && oldProgress && !motionOff()) {
        const target = newFill.style.getPropertyValue('--progress');
        if (target !== oldProgress) {
          newFill.style.setProperty('--progress', oldProgress);
          requestAnimationFrame(() =>
            requestAnimationFrame(() => newFill.style.setProperty('--progress', target))
          );
        }
      }

      document.dispatchEvent(
        new CustomEvent('brick:cart:updated', {
          detail: {
            itemCount: parseInt(adopted.dataset.itemCount, 10) || 0,
            countText: adopted.dataset.countText || '',
            source: 'cart-page',
          },
        })
      );

      if (hadFocus || (focus && focus.title)) this.restoreFocus(focus);
      return true;
    }

    findLine(key) {
      if (!key) return null;
      return this.querySelector(`[data-line][data-key="${CSS.escape(key)}"]`);
    }

    restoreFocus(focus) {
      const title = this.querySelector('.cart-page__title');
      if (!focus || focus.title) {
        title?.focus({ preventScroll: Boolean(focus && focus.title) });
        return;
      }
      const selectors = {
        plus: '[data-qty-step="1"]',
        minus: '[data-qty-step="-1"]',
        input: '[data-qty-input]',
        remove: '[data-remove]',
      };
      const line = this.findLine(focus.key);
      let target = null;
      if (line) {
        target = line.querySelector(selectors[focus.role] || '[data-qty-input]');
        if (target && target.disabled) target = line.querySelector('[data-qty-input]');
      } else {
        target = this.querySelector('[data-line] [data-remove]');
      }
      (target || title)?.focus({ preventScroll: true });
    }
  }

  customElements.define('brick-cart-page', BrickCartPage);
})();
